import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { quotations as qApi, suppliers as sApi, agents as agentsApi, samples as samplesApi } from '../utils/api';
import { StatusBadge, STATUS_LIST, STATUS_META, STATUS_PT, fmtBRL, fmtUSD, fmtRMB, fmtDate, totalDays, calcItemBRL, calcItemUSD } from '../utils/helpers';
import QuotationForm from '../components/QuotationForm';
import { useToast, ToastContainer } from '../hooks/useToast';
import OrderTab from '../components/OrderTab';
import PaymentsTab from '../components/PaymentsTab';
import ShipmentTab from '../components/ShipmentTab';
import DownloadsPanel from '../components/DownloadsPanel';
import PrintReport from '../components/PrintReport';
import BulkOrderModal from '../components/BulkOrderModal';
import ExportOptionsModal from '../components/ExportOptionsModal';
import { useT } from '../i18n/I18nContext';
import { gridLines, isTechPack } from '../utils/sizeGrid';

const Q_PAGE_LIMIT = 20;
const FILTER_KEY = 'charth_quotation_filters';

function loadSavedFilters() {
  try { return JSON.parse(localStorage.getItem(FILTER_KEY)) || {}; } catch { return {}; }
}
function saveFilter(key, value) {
  try { const s = loadSavedFilters(); s[key] = value; localStorage.setItem(FILTER_KEY, JSON.stringify(s)); } catch {}
}

export default function QuotationsPage() {
  const t = useT();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [qTotal, setQTotal]         = useState(0);
  const [qPage, setQPage]           = useState(1);
  const [qPages, setQPages]         = useState(1);
  const [suppliers, setSuppliers]   = useState([]);
  const [agents, setAgents]         = useState([]);
  const [selected, setSelected]     = useState(null);
  const [showForm, setShowForm]     = useState(false);
  const [editData, setEditData]     = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting]   = useState(false);
  const [filter, setFilterRaw]           = useState(() => loadSavedFilters().text || '');
  const [statusFilter, setStatusFilterRaw] = useState(() => loadSavedFilters().status || null);
  const { toasts, toast }           = useToast();

  function setFilter(v) { setFilterRaw(v); saveFilter('text', v); }
  function setStatusFilter(v) { setStatusFilterRaw(v); saveFilter('status', v); }
  function clearFilters() {
    setFilterRaw(''); setStatusFilterRaw(null);
    localStorage.removeItem(FILTER_KEY);
  }

  const loadQuotations = useCallback(async (pageOverride) => {
    const p = pageOverride !== undefined ? pageOverride : qPage;
    try {
      const data = await qApi.list({ page: p, limit: Q_PAGE_LIMIT, type: 'quotations' });
      setQuotations(data?.rows || []);
      setQTotal(data?.total || 0);
      setQPage(data?.page || p);
      setQPages(data?.pages || 1);
    } catch (e) {
      setQuotations([]); setQTotal(0); setQPages(1);
    }
  }, []); // eslint-disable-line

  const load = useCallback(async (pageOverride) => {
    const [ss, as] = await Promise.all([sApi.list({ limit: 200 }), agentsApi.list()]);
    setSuppliers(ss.rows || ss); setAgents(as);
    await loadQuotations(pageOverride);
  }, [loadQuotations]);

  useEffect(() => {
    load();
    if (searchParams.get('new')==='1') { setShowForm(true); setEditData(null); setSearchParams({}); }
  }, [load]);

  useEffect(() => {
    if (id) qApi.get(id).then(q => {
      // Consistência de tela: se já virou pedido (sample/bulk), abre na tela de pedido
      if (q.order_type && q.order_type !== 'quotation') { navigate(`/orders/${id}`, { replace: true }); return; }
      setSelected(q);
    }).catch(() => setSelected(null));
    else setSelected(null);
  }, [id, navigate]);

  async function handleSave(formData) {
    try {
      if (editData?.id) {
        await qApi.update(editData.id, formData);
        toast(t('quotations.saved'),'success');
        if (selected?.id===editData.id) setSelected(await qApi.get(editData.id));
        setShowForm(false); setEditData(null); load(1);
        return null;
      } else {
        const result = await qApi.create(formData);
        toast(t('quotations.created'),'success');
        return result;
      }
    } catch(e) { toast(t('quotations.error.save'),'error'); throw e; }
  }

  async function handleExport(currencyMode, lang) {
    if (!selected) return;
    setExporting(true);
    try {
      const result = await qApi.export(selected.id, currencyMode, lang);
      const blobUrl = URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      toast(t('quotations.export.success'),'success');
      setShowExportModal(false);
      loadQuotations();
      setSelected(await qApi.get(selected.id));
    } catch(e) { toast('Export error: '+e.message,'error'); }
    finally { setExporting(false); }
  }

  async function handleDelete(qid) {
    if (!window.confirm(t('quotations.delete.confirm'))) return;
    await qApi.delete(qid); toast(t('quotations.deleted'),'default'); navigate('/quotations'); load(1);
  }

  async function handleStatusChange(qid, status) {
    await qApi.setStatus(qid, status);
    toast(`Status: ${status}`,'success');
    if (selected?.id===qid) setSelected(await qApi.get(qid));
    loadQuotations();
  }

  const filtered = quotations.filter(q => {
    const matchText = !filter ||
      q.reference.toLowerCase().includes(filter.toLowerCase()) ||
      q.supplier_name.toLowerCase().includes(filter.toLowerCase()) ||
      (q.collection||'').toLowerCase().includes(filter.toLowerCase());
    const matchStatus = !statusFilter || q.status === statusFilter;
    return matchText && matchStatus;
  });

  if (selected) return (
    <QuotationDetail
      quotation={selected} suppliers={suppliers} agents={agents}
      onBack={()=> searchParams.get('from') === 'samples' ? navigate('/samples') : navigate('/quotations')}
      onEdit={()=>{ setEditData(selected); setShowForm(true); }}
      onExport={()=>setShowExportModal(true)}
      onDelete={()=>handleDelete(selected.id)}
      onStatusChange={s=>handleStatusChange(selected.id,s)}
      showForm={showForm && editData?.id===selected.id}
      onSave={handleSave}
      onCloseForm={()=>{ setShowForm(false); setEditData(null); }}
      toast={toast}
      onRefresh={async () => { const fresh = await qApi.get(selected.id); setSelected(fresh); }}
    >
      {showExportModal && (
        <ExportOptionsModal onExport={handleExport} onClose={()=>setShowExportModal(false)} exporting={exporting}
          multiplier={selected?.other_costs_multiplier||1.6} currency={selected?.currency||'USD'}
          kind={selected?.order_type && selected.order_type !== 'quotation' ? 'order' : 'quotation'} />
      )}
    </QuotationDetail>
  );

  const subtitle = qPages > 1
    ? `${qTotal} ${t('quotations.title').toLowerCase()} · ${t('pagination.of').replace('de','página')} ${qPage} ${t('pagination.of')} ${qPages}`
    : `${qTotal} ${t('quotations.title').toLowerCase()}`;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">📋 {t('quotations.title')}</div>
          <div className="page-subtitle">{subtitle}</div>
        </div>
        <button className="btn btn-primary" onClick={()=>{ setEditData(null); setShowForm(true); }}>{t('quotations.new')}</button>
      </div>

      <div style={{marginBottom:16,display:'flex',flexDirection:'column',gap:10}}>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <input
            placeholder={t('search.quotation')}
            value={filter}
            onChange={e=>setFilter(e.target.value)}
            style={{maxWidth:460,flex:'0 0 auto'}}
          />
          {(filter||statusFilter) && (
            <button className="btn btn-ghost btn-sm" onClick={clearFilters}>{t('filter.clear')}</button>
          )}
        </div>
        <div style={{display:'flex',gap:7,flexWrap:'wrap',alignItems:'center',overflowX:'auto',paddingBottom:2}}>
          <button
            className={`status-chip${!statusFilter?' status-chip--active':''}`}
            onClick={()=>setStatusFilter(null)}
          >
            {t('quotations.filter.all')} ({qTotal})
          </button>
          {STATUS_LIST.map(s=>{
            const count = quotations.filter(q=>q.status===s).length;
            if (count===0) return null;
            return (
              <button
                key={s}
                className={`status-chip${statusFilter===s?' status-chip--active':''}`}
                onClick={()=>setStatusFilter(statusFilter===s?null:s)}
                title={s}
              >
                {STATUS_META[s]?.emoji} {STATUS_PT[s]||s}
                <span style={{
                  marginLeft:5,padding:'1px 6px',borderRadius:999,fontSize:11,
                  background: statusFilter===s?'rgba(255,255,255,.25)':'var(--gray-200)',
                  color: statusFilter===s?'white':'var(--gray-600)',
                  fontWeight:700,
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="card" style={{padding:0}}>
        {filtered.length===0 ? (
          <div className="empty-state"><div className="empty-icon">📋</div><div className="empty-title">{t('quotations.empty')}</div><div className="empty-sub">{t('quotations.empty.sub')}</div></div>
        ) : (
          <div className="table-wrap" style={{border:'none',borderRadius:'var(--radius-lg)'}}>
            <table>
              <thead><tr>
                <th>{t('col.reference')}</th>
                <th>{t('col.supplier')}</th>
                <th>{t('col.collection')}</th>
                <th>{t('col.cost_center')}</th>
                <th>{t('col.items')}</th>
                <th>{t('col.photos')}</th>
                <th>{t('col.exchange')}</th>
                <th>{t('col.est_cost_brl')}</th>
                <th>Status</th>
                <th>{t('col.created_at')}</th>
              </tr></thead>
              <tbody>
                {filtered.map(q=>(
                  <tr key={q.id} onClick={()=>navigate(`/quotations/${q.id}`)}>
                    <td className="mono">{q.reference}</td>
                    <td className="bold">{q.supplier_name}</td>
                    <td><span className="tag">{q.collection||'—'}</span></td>
                    <td>{q.cost_center==='producao'?<span style={{padding:'2px 8px',borderRadius:999,fontSize:11,fontWeight:600,background:'#2563eb22',color:'#2563eb'}}>🏭 Produção</span>:q.cost_center==='marketing'?<span style={{padding:'2px 8px',borderRadius:999,fontSize:11,fontWeight:600,background:'#9333ea22',color:'#9333ea'}}>📣 Marketing</span>:<span style={{fontSize:12,color:'var(--gray-400)'}}>—</span>}</td>
                    <td>{q.item_count}</td>
                    <td><QuotationPhotoHint quotationId={q.id} photoCount={q.photo_count} firstPhoto={q.first_photo} /></td>
                    <td className="mono">R$ {Number(q.exchange_rate).toFixed(2)}</td>
                    <td className="text-green">{fmtBRL(q.total_estimated_brl)}</td>
                    <td><StatusBadge status={q.status} /></td>
                    <td className="text-muted">{fmtDate(q.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {qPages > 1 && (
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12,marginTop:16}}>
          <button className="btn btn-ghost btn-sm" onClick={()=>{const p=qPage-1;setQPage(p);loadQuotations(p);}} disabled={qPage<=1}>{t('pagination.prev')}</button>
          <span style={{fontSize:13,color:'var(--gray-500)'}}>{qPage} {t('pagination.of')} {qPages}</span>
          <button className="btn btn-ghost btn-sm" onClick={()=>{const p=qPage+1;setQPage(p);loadQuotations(p);}} disabled={qPage>=qPages}>{t('pagination.next')}</button>
        </div>
      )}
      {showForm && !editData?.id && (
        <QuotationForm
          quotation={null} suppliers={suppliers} agents={agents} onSave={handleSave}
          onClose={()=>setShowForm(false)}
          onAfterCreate={id=>{ setShowForm(false); setEditData(null); load(1); navigate(`/quotations/${id}`); }}
        />
      )}
      <ToastContainer toasts={toasts} />
    </div>
  );
}

function QuotationPhotoHint({ quotationId, photoCount, firstPhoto }) {
  if (!photoCount || photoCount === 0) return <span style={{color:'var(--gray-300)',fontSize:12}}>—</span>;
  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        cursor: 'default',
        fontSize: 12,
        color: 'var(--pantone)',
        fontWeight: 600,
        userSelect: 'none',
      }}
      className="photo-hint"
    >
      📷 {photoCount}
      {firstPhoto && (
        <span className="photo-hint-tooltip">
          <img
            src={`/uploads/${firstPhoto}`}
            alt="preview"
            style={{
              width: 80,
              height: 80,
              objectFit: 'cover',
              borderRadius: 6,
              display: 'block',
              border: '2px solid white',
            }}
          />
        </span>
      )}
    </span>
  );
}

function QuotationDetail({ quotation:q, suppliers, agents = [], onBack, onEdit, onExport, onDelete, onStatusChange, showForm, onSave, onCloseForm, toast, onRefresh, children }) {
  const t = useT();
  const importRef = useRef();
  const [importing, setImporting] = useState(false);
  const isRMB = q.currency === 'RMB';
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'quotation');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const isOrder = q.order_type && q.order_type !== 'quotation';
  const hasSampleItems = (q.items || []).some(it => it.sample_requested);
  const showTabs = isOrder || q.status === 'Approved' || hasSampleItems;

  async function handleImportResponse(e) {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const result = await qApi.importResponse(q.id, fd);
      toast(`${t('quotations.imported').replace('itens','').replace('items','').trim()}: ${result.updated} ${t('quotations.imported')}`, 'success');
      onStatusChange('Response Received');
    } catch(err) { toast(t('quotations.import_error') + (err.response?.data?.error || err.message), 'error'); }
    finally { setImporting(false); e.target.value=''; }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="gap-8">
          <button className="btn btn-ghost btn-sm" onClick={onBack}>
            {searchParams.get('from') === 'samples' ? `← ${t('nav.samples')}` : t('btn.back')}
          </button>
          <div>
            <div className="page-title" style={{fontFamily:'var(--font-mono)',fontSize:18}}>{q.reference}</div>
            <div className="page-subtitle">{q.supplier_name} · {q.collection||t('quotations.no_collection')}{q.cost_center&&<span style={{marginLeft:8,padding:'1px 7px',borderRadius:999,fontSize:11,fontWeight:600,background:q.cost_center==='producao'?'#2563eb22':'#9333ea22',color:q.cost_center==='producao'?'#2563eb':'#9333ea'}}>{q.cost_center==='producao'?'🏭 Produção':'📣 Marketing'}</span>}</div>
          </div>
        </div>
        <div className="action-group">
          {isOrder ? (
            <span style={{fontSize:12,color:'var(--gray-400)',padding:'8px 12px',background:'var(--gray-100)',borderRadius:'var(--radius)'}}>
              {t('quotations.locked_status')}
            </span>
          ) : (
            <select value={q.status} onChange={e=>onStatusChange(e.target.value)}
              style={{fontSize:13,borderColor:'var(--gray-300)',borderRadius:'var(--radius)',padding:'8px 12px'}}>
              {STATUS_LIST.map(s=><option key={s}>{s}</option>)}
            </select>
          )}
          <input ref={importRef} type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={handleImportResponse} />
          <button className="btn btn-ghost btn-sm" onClick={()=>importRef.current?.click()} disabled={importing}>
            {importing?'⏳':'📥'} {t('quotations.import_response')}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={onEdit}>✏️ {t('btn.edit')}</button>
          {/* Transformar esta cotação/amostra num PEDIDO BULK novo, escolhendo os itens */}
          {q.order_type !== 'bulk' && (q.items || []).length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowBulkModal(true)} title={t('bulk.open_hint')}>
              🔵 {t('bulk.open_btn')}
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => window.print()}>{t('print.btn')}</button>
          <button className="btn btn-accent" onClick={onExport}>📥 {t('btn.export')}</button>
          <button className="btn btn-danger btn-sm btn-icon" onClick={onDelete} title={t('btn.delete')}>🗑️</button>
        </div>
      </div>

      {showBulkModal && (
        <BulkOrderModal
          quotation={q}
          toast={toast}
          onClose={() => setShowBulkModal(false)}
          onCreated={result => { setShowBulkModal(false); window.location.href = `/orders/${result.id}`; }}
        />
      )}

      <DownloadsPanel quotation={q} toast={toast} />

      {showTabs && (
        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--gray-100)', marginBottom: 20 }}>
          {[
            { key: 'quotation', label: t('quotations.tab.quotation') },
            ...(hasSampleItems ? [{ key: 'samples', label: t('quotations.tab.samples') }] : []),
            { key: 'order',     label: t('quotations.tab.order') },
            ...(isOrder ? [
              { key: 'payments', label: t('quotations.tab.payments') },
              { key: 'shipment', label: t('quotations.tab.shipment') },
            ] : []),
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '10px 20px', background: 'none', border: 'none',
                borderBottom: activeTab === tab.key ? '2px solid var(--pantone)' : '2px solid transparent',
                color: activeTab === tab.key ? 'var(--pantone)' : 'var(--gray-400)',
                fontWeight: activeTab === tab.key ? 700 : 400,
                cursor: 'pointer', fontSize: 13, marginBottom: -2,
                fontFamily: 'var(--font)',
              }}>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {(!showTabs || activeTab === 'quotation') && (<><div className="detail-grid">
        <div className="card">
          <div className="card-header"><div className="card-title">{t('quotations.detail.supplier_card')}</div></div>
          <div className="card-body">
            <div style={{fontWeight:700,fontSize:15,color:'var(--pantone)',marginBottom:4}}>{q.supplier_name}</div>
            <div className="text-muted">{q.supplier_country}</div>
            {q.contact_name && <div style={{marginTop:8,fontSize:13}}>👤 {q.contact_name}</div>}
            {q.contact_email && <div style={{fontSize:13,color:'var(--blue)'}}>✉️ {q.contact_email}</div>}
            {q.contact_whatsapp && <div style={{fontSize:13}}>💬 {q.contact_whatsapp}</div>}
          </div>
        </div>
        <div className="card">
          <div className="card-header"><div className="card-title">{t('quotations.detail.parameters')}</div></div>
          <div className="card-body">
            <div style={{display:'flex',gap:24,marginBottom:10,flexWrap:'wrap'}}>
              <div>
                <div className="text-muted">{t('quotations.detail.currency')}</div>
                <div style={{fontWeight:700,fontSize:15,fontFamily:'var(--font-mono)',color: q.currency==='RMB'?'#b34700':'var(--navy)'}}>{q.currency==='RMB'?'¥ RMB':'$ USD'}</div>
              </div>
              <div><div className="text-muted">{t('quotations.detail.exchange_rate')}</div><div style={{fontWeight:700,fontSize:15,fontFamily:'var(--font-mono)'}}>R$ {Number(q.exchange_rate).toFixed(2)}</div></div>
              {q.currency==='RMB' && <div><div className="text-muted">{t('quotations.detail.rmb_rate')}</div><div style={{fontWeight:700,fontSize:15,fontFamily:'var(--font-mono)'}}>¥ {Number(q.rmb_rate||6.7).toFixed(2)}</div></div>}
              <div><div className="text-muted">{t('quotations.detail.other_costs')}</div><div style={{fontWeight:700,fontSize:15,fontFamily:'var(--font-mono)'}} title="Frete + impostos + taxas">×{Number(q.other_costs_multiplier||1.6).toFixed(2)} <span style={{fontWeight:400,fontSize:12,color:'var(--gray-500)'}}>+{Math.round(((q.other_costs_multiplier||1.6)-1)*100)}%</span></div></div>
              {(() => {
                const transitDays = (q.shipping_days||0) + (q.customs_days||0);
                const items = q.items || [];
                const maxLead = items.length
                  ? Math.max(...items.map(it => totalDays(it, q.shipping_days, q.customs_days)))
                  : transitDays;
                return (
                  <div>
                    <div className="text-muted">Total Lead Time</div>
                    <div style={{fontWeight:700,fontSize:15}}>{maxLead > 0 ? `${maxLead}d` : '—'}</div>
                    <div style={{fontSize:11,color:'var(--gray-400)'}}>{t('quotations.detail.lead_sub')}</div>
                  </div>
                );
              })()}
              <div><div className="text-muted">Status</div><StatusBadge status={q.status} /></div>
            </div>
            {q.notes && <div style={{fontSize:13,color:'var(--gray-600)',background:'var(--gray-50)',borderRadius:'var(--radius)',padding:10}}>💬 {q.notes}</div>}
          </div>
        </div>
      </div>

      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">📦 {t('col.items')} ({q.items?.length||0})</div></div>
        <div className="card-body" style={{padding:0}}>
          <div className="table-wrap" style={{border:'none'}}>
            <table>
              <thead><tr>
                <th>{t('col.num')}</th>
                <th>{t('quotations.col.product')}</th>
                <th style={{textAlign:'center'}}>🧪</th>
                <th>{t('quotations.col.spec')}</th>
                <th>{t('col.hs_code')}</th>
                <th>{t('col.moq')}</th>
                <th>{t('col.qty')}</th>
                <th>{t('quotations.col.init_price')}</th>
                <th>{t('quotations.col.target_price')}</th>
                <th>{t('quotations.col.confirmed_price')}</th>
                <th>{t('col.sampling_days')}</th>
                <th>{t('quotations.col.production_days')}</th>
                <th>{t('col.lead_time')}</th>
                <th>Est. {isRMB?'RMB':'USD'}</th>
                <th>{t('col.unit_brl')}</th>
                <th>Est. BRL</th>
                <th>{t('quotations.col.files')}</th>
              </tr></thead>
              <tbody>
                {(q.items||[]).map((item,i)=>{
                  const lead = totalDays(item,q.shipping_days,q.customs_days);
                  const brl  = calcItemBRL(item,q.exchange_rate,q.other_costs_multiplier,q.rmb_rate,q.currency);
                  const priceUSD = q.currency==='RMB' ? (item.target_price||0)/(q.rmb_rate||6.7) : (item.target_price||0);
                  const unitBRL  = priceUSD * (q.exchange_rate||5.2) * (q.other_costs_multiplier||1.6);
                  const nativeAmt = (item.target_price||0) * (item.quantity||0);
                  return (
                    <tr key={item.id}>
                      <td className="mono">{i+1}</td>
                      <td className="bold">
                        {item.product}
                        {item.is_development ? <span style={{marginLeft:6,background:'var(--pantone)',color:'white',borderRadius:4,padding:'1px 6px',fontSize:10,fontWeight:700,verticalAlign:'middle'}}>DEV</span> : null}
                        {item.is_garment ? <span title="Roupa pronta" style={{marginLeft:6,background:'var(--accent-light,#f7f0f2)',color:'var(--pantone)',border:'1px solid var(--accent)',borderRadius:4,padding:'1px 6px',fontSize:10,fontWeight:700,verticalAlign:'middle'}}>👗 ROUPA PRONTA</span> : null}
                        {item.is_garment ? gridLines(item).map((line, li) => (
                          <div key={li} data-testid="detail-size-grid" style={{fontSize:11,fontWeight:500,color:'var(--gray-500)',marginTop:3,fontFamily:'var(--font-mono)'}}>
                            {li === 0 ? 'Grade: ' : ''}{line}
                          </div>
                        )) : null}
                        {(item.files||[]).filter(isTechPack).map(f => (
                          <div key={f.id} style={{marginTop:3}}>
                            <a href={`/uploads/${f.filename}`} target="_blank" rel="noreferrer" style={{fontSize:11,fontWeight:600,color:'var(--pantone)'}}>📐 Ficha técnica: {f.original_name}</a>
                          </div>
                        ))}
                      </td>
                      <td style={{textAlign:'center'}}>{item.sample_requested ? <span title="Sample" style={{fontSize:16}}>🧪</span> : null}</td>
                      <td style={{fontSize:12,color:'var(--gray-500)'}}>{item.specification||'—'}</td>
                      <td className="mono">{item.ncm||'—'}</td>
                      <td>{item.moq||'—'}</td>
                      <td style={{fontWeight:600}}>{item.quantity||'—'}</td>
                      <td className="mono" style={{color:'var(--gray-400)'}}>{item.initial_price?(isRMB?fmtRMB(item.initial_price):fmtUSD(item.initial_price)):'—'}</td>
                      <td className="mono" style={{fontWeight:700}}>{item.target_price?(isRMB?fmtRMB(item.target_price):fmtUSD(item.target_price)):'—'}</td>
                      <td className="mono" style={{fontWeight:700,color:item.confirmed_price?'var(--green)':'var(--gray-300)'}}>{item.confirmed_price?(isRMB?fmtRMB(item.confirmed_price):fmtUSD(item.confirmed_price)):'—'}</td>
                      <td>{item.sampling_days?`${item.sampling_days}d`:'—'}</td>
                      <td>{item.production_days?`${item.production_days}d`:'—'}</td>
                      <td style={{fontWeight:700}}>{lead>0?`${lead}d`:'—'}</td>
                      <td className="mono" style={{color:'var(--blue)',fontSize:12}}>{nativeAmt>0?(isRMB?fmtRMB(nativeAmt):fmtUSD(nativeAmt)):'—'}</td>
                      <td className="mono" style={{color:'var(--pantone)',fontWeight:700,fontSize:12}}>{unitBRL>0?fmtBRL(unitBRL):'—'}</td>
                      <td className="text-green" style={{fontSize:12}}>{brl>0?fmtBRL(brl):'—'}</td>
                      <td>
                        {(item.files||[]).length > 0 ? (
                          <div style={{display:'flex',flexWrap:'wrap',gap:3,justifyContent:'center'}}>
                            {(item.files||[]).map(f =>
                              isTechPack(f) ? (
                                <a key={f.id} href={`/uploads/${f.filename}`} target="_blank" rel="noreferrer"
                                  title={`Ficha técnica: ${f.original_name}`} style={{fontSize:22,lineHeight:'36px',display:'inline-block',textDecoration:'none'}}>📐</a>
                              ) : f.file_type==='image' ? (
                                <a key={f.id} href={`/uploads/${f.filename}`} target="_blank" rel="noreferrer" title={f.original_name}>
                                  <img src={`/uploads/${f.filename}`} alt={f.original_name}
                                    style={{width:36,height:36,objectFit:'cover',borderRadius:3,border:'1px solid var(--gray-200)',display:'block'}} />
                                </a>
                              ) : (
                                <a key={f.id} href={`/uploads/${f.filename}`} download={f.original_name}
                                  title={f.original_name} style={{fontSize:22,lineHeight:'36px',display:'inline-block',textDecoration:'none'}}>📄</a>
                              )
                            )}
                          </div>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',gap:10,padding:'14px 20px',borderTop:'1px solid var(--gray-100)'}}>
            <div style={{background:'var(--gray-100)',borderRadius:'var(--radius)',padding:'8px 16px',fontFamily:'var(--font-mono)',fontSize:13,fontWeight:700,color:'var(--navy)'}}>
              {isRMB ? '¥ RMB' : '$ USD'}:{' '}
              {isRMB
                ? fmtRMB((q.items||[]).reduce((s,item)=>s+(item.target_price||0)*(item.quantity||0),0))
                : fmtUSD((q.items||[]).reduce((s,item)=>s+calcItemUSD(item,q.rmb_rate,q.currency),0))
              }
            </div>
            <div style={{background:'var(--pantone)',color:'white',borderRadius:'var(--radius)',padding:'8px 16px',fontFamily:'var(--font-mono)',fontSize:13,fontWeight:700}}>
              BRL Est.: {fmtBRL(q.total_estimated_brl)}
            </div>
          </div>
        </div>
      </div>

      {q.photos?.length>0 && (
        <div className="card">
          <div className="card-header"><div className="card-title">{t('quotations.detail.attachments')} ({q.photos.length})</div></div>
          <div className="card-body">
            <div className="photo-grid">
              {q.photos.map(p=>{
                const isImg = /\.(jpg|jpeg|png|gif|webp|heic)$/i.test(p.filename||p.original_name||'');
                return isImg ? (
                  <a key={p.id} href={`/uploads/${p.filename}`} target="_blank" rel="noreferrer" title={p.original_name}>
                    <img src={`/uploads/${p.filename}`} alt={p.original_name} className="photo-thumb" />
                  </a>
                ) : (
                  <a key={p.id} href={`/uploads/${p.filename}`} download={p.original_name} title={p.original_name}
                    style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,padding:'8px 10px',border:'1px solid var(--gray-200)',borderRadius:'var(--radius)',fontSize:11,color:'var(--navy)',textDecoration:'none',textAlign:'center',minWidth:76,cursor:'pointer',background:'var(--gray-50)'}}>
                    <span style={{fontSize:28}}>📄</span>
                    <span style={{wordBreak:'break-word',maxWidth:76,lineHeight:1.3}}>{p.original_name}</span>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      )}

      </>)}

      {showTabs && activeTab === 'samples' && (
        <SampleTrackingTab quotation={q} onRefresh={onRefresh} toast={toast} />
      )}

      {showTabs && activeTab === 'order' && (
        <OrderTab quotation={q} onRefresh={onRefresh} />
      )}
      {isOrder && activeTab === 'payments' && (
        <PaymentsTab quotation={q} onRefresh={onRefresh} toast={toast} />
      )}
      {isOrder && activeTab === 'shipment' && (
        <ShipmentTab quotation={q} onRefresh={onRefresh} />
      )}

      {showForm && <QuotationForm quotation={q} suppliers={suppliers} agents={agents} onSave={onSave} onClose={onCloseForm} onFilesUploaded={onRefresh} />}
      {children}

      <PrintReport quotation={q} />
    </div>
  );
}

const SAMPLE_STATUS_OPTS_KEYS = [
  { value: 'pending',  color: '#92400E', bg: '#FFFBEB' },
  { value: 'approved', color: '#276749', bg: '#F0FFF4' },
  { value: 'rejected', color: '#C53030', bg: '#FFF5F5' },
];

const TRACKING_ALERT_META = {
  customs_hold:   { bg: '#FFF3E0', color: '#E65100', icon: '📦' },
  customs_action: { bg: '#FFF9C4', color: '#F57F17', icon: '⚠️' },
  payment_due:    { bg: '#FCE4EC', color: '#AD1457', icon: '💰' },
};

function TrackingCard({ trackingData, onRefresh, refreshing, t }) {
  const [expanded, setExpanded] = useState(false);
  const events = (() => {
    if (!trackingData?.events) return [];
    try { return typeof trackingData.events === 'string' ? JSON.parse(trackingData.events) : trackingData.events; }
    catch { return []; }
  })();
  const alertMeta = trackingData?.alert_type ? TRACKING_ALERT_META[trackingData.alert_type] : null;

  return (
    <div className="card" style={{ marginBottom: 16, padding: '14px 20px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <span style={{ fontWeight:700, fontSize:13, color:'var(--pantone)' }}>📡 {t('tracking.live_status')}</span>
        {trackingData ? (
          <>
            {trackingData.carrier && (
              <span style={{ fontSize:12, fontWeight:600, color:'var(--gray-600)',
                background:'var(--gray-100)', padding:'2px 8px', borderRadius:8 }}>
                {trackingData.carrier}
              </span>
            )}
            {trackingData.status_text && (
              <span style={{
                padding:'3px 10px', borderRadius:10, fontSize:12, fontWeight:600,
                background: alertMeta ? alertMeta.bg : '#EBF8FF',
                color: alertMeta ? alertMeta.color : '#2B6CB0',
              }}>
                {trackingData.status_text}
              </span>
            )}
            {trackingData.estimated_delivery && (
              <span style={{ fontSize:12, color:'var(--gray-500)' }}>
                {t('tracking.edd')}: <strong>{trackingData.estimated_delivery.slice(0,10)}</strong>
              </span>
            )}
            {!trackingData.status_text && trackingData.tracking_number && (
              <span style={{ fontSize:12, color:'var(--gray-400)' }}>{trackingData.tracking_number}</span>
            )}
            {events.length > 0 && (
              <button onClick={() => setExpanded(v => !v)}
                style={{ fontSize:12, color:'var(--pantone)', background:'none', border:'none',
                  cursor:'pointer', padding:'2px 6px', fontWeight:600 }}>
                {expanded ? '▲' : '▼'} {events.length} eventos
              </button>
            )}
            {trackingData.tracking_url && (
              <a href={trackingData.tracking_url} target="_blank" rel="noreferrer"
                className="btn btn-ghost btn-sm" style={{ textDecoration:'none', flexShrink:0 }}>
                🔗 {t('tracking.open_carrier')}
              </a>
            )}
            {trackingData.last_refreshed && (
              <span style={{ fontSize:11, color:'var(--gray-400)', marginLeft:'auto' }}>
                {trackingData.last_refreshed.slice(0,16).replace('T',' ')}
              </span>
            )}
          </>
        ) : (
          <span style={{ fontSize:12, color:'var(--gray-400)' }}>{t('tracking.no_tracking')}</span>
        )}
        <button className="btn btn-ghost btn-sm" onClick={onRefresh} disabled={refreshing}
          style={{ marginLeft: trackingData ? 0 : 'auto', flexShrink:0 }}>
          {refreshing ? t('tracking.refreshing') : t('tracking.refresh')}
        </button>
      </div>

      {alertMeta && (
        <div style={{
          marginTop:10, padding:'10px 14px', borderRadius:'var(--radius)',
          background:alertMeta.bg, color:alertMeta.color, border:`1.5px solid ${alertMeta.color}44`,
          display:'flex', alignItems:'center', gap:10,
        }}>
          <span style={{ fontSize:18 }}>{alertMeta.icon}</span>
          <span style={{ fontWeight:600, fontSize:13 }}>
            {t(`tracking.alert.${trackingData.alert_type}`)}
            {trackingData.alert_message ? ` — ${trackingData.alert_message}` : ''}
          </span>
          {trackingData.alert_link && (
            <a href={trackingData.alert_link} target="_blank" rel="noreferrer"
              style={{ marginLeft:'auto', fontSize:12, color:alertMeta.color, fontWeight:700, textDecoration:'none' }}>
              {t('tracking.alert.action_link')}
            </a>
          )}
        </div>
      )}

      {expanded && events.length > 0 && (
        <div style={{ marginTop:12, borderTop:'1px solid var(--gray-100)', paddingTop:12 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--gray-500)', textTransform:'uppercase',
            letterSpacing:1, marginBottom:8 }}>
            {t('tracking.events_title')}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
            {events.map((ev, i) => {
              const time = ev.checkpoint_time || ev.a || ev.time || '';
              const msg  = ev.message || ev.z || ev.desc || ev.standerdDesc || '';
              const city = ev.city || ev.l || ev.cityName || '';
              const loc  = [city, ev.country_name].filter(Boolean).join(', ');
              return (
                <div key={i} style={{
                  display:'grid', gridTemplateColumns:'140px 1fr',
                  gap:'4px 16px', padding:'8px 0',
                  borderBottom: i < events.length - 1 ? '1px solid var(--gray-100)' : 'none',
                }}>
                  <div style={{ fontSize:11, color:'var(--gray-400)', paddingTop:2 }}>
                    {time ? new Date(time).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}
                  </div>
                  <div>
                    <div style={{ fontSize:13, color: i===0 ? 'var(--pantone)' : 'var(--gray-700)',
                      fontWeight: i===0 ? 600 : 400 }}>
                      {msg || '—'}
                    </div>
                    {loc && <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:2 }}>{loc}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SampleTrackingTab({ quotation: q, onRefresh, toast }) {
  const t = useT();
  const SAMPLE_STATUS_OPTS = [
    { value: 'pending',  label: t('sample.status.pending'),  color: '#92400E', bg: '#FFFBEB' },
    { value: 'approved', label: t('sample.status.approved'), color: '#276749', bg: '#F0FFF4' },
    { value: 'rejected', label: t('sample.status.rejected'), color: '#C53030', bg: '#FFF5F5' },
  ];

  const sampleItems = (q.items || []).filter(it => it.sample_requested);
  const [tracking, setTracking] = useState({ tracking_number:'', carrier:'', dispatched_at:'', received_at:'', cost_usd:'', notes:'' });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [liveTracking, setLiveTracking] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    samplesApi.getTracking(q.id).then(data => {
      if (data.tracking) setTracking({ ...data.tracking, cost_usd: data.tracking.cost_usd || '' });
      const mapped = sampleItems.map(it => {
        const r = (data.items || []).find(x => x.id === it.id);
        return r ? { ...it, sample_status: r.sample_status, qty_sampled: r.qty_sampled || 1, sample_notes: r.sample_notes || '' }
                 : { ...it, sample_status: 'pending', qty_sampled: 1, sample_notes: '' };
      });
      setResults(mapped);
    }).catch(() => {
      setResults(sampleItems.map(it => ({ ...it, sample_status: 'pending', qty_sampled: 1, sample_notes: '' })));
    }).finally(() => setLoading(false));
  }, [q.id]); // eslint-disable-line

  useEffect(() => {
    samplesApi.getLiveTracking(q.id).then(data => setLiveTracking(data)).catch(() => {});
  }, [q.id]);

  async function handleRefreshTracking() {
    setRefreshing(true);
    try {
      const body = tracking.tracking_number ? { tracking_number: tracking.tracking_number, carrier: tracking.carrier } : {};
      const data = await samplesApi.refreshTracking(q.id, body);
      setLiveTracking(data);
      toast(t('tracking.refreshed'), 'success');
    } catch(err) {
      const code = err.response?.data?.error;
      if (code === 'no_tracking_number') toast(t('tracking.no_tracking'), 'error');
      else if (code === 'tracking_api_not_configured') toast(t('tracking.no_key'), 'warning');
      else toast(t('tracking.refresh_error'), 'error');
    } finally { setRefreshing(false); }
  }

  function setResult(idx, k, v) {
    setResults(prev => prev.map((r, i) => i === idx ? { ...r, [k]: v } : r));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await samplesApi.saveTracking(q.id, { tracking, items: results });
      toast(t('quotations.sample.saved'), 'success');
      onRefresh();
    } catch(e) {
      toast(t('quotations.sample.error') + (e.response?.data?.error || e.message), 'error');
    } finally { setSaving(false); }
  }

  if (loading) return <div style={{padding:40,textAlign:'center',color:'var(--gray-400)'}}>{t('common.loading')}</div>;

  return (
    <div>
      <TrackingCard
        trackingData={liveTracking}
        onRefresh={handleRefreshTracking}
        refreshing={refreshing}
        t={t}
      />

      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">{t('quotations.sample.tracking_title')}</div></div>
        <div className="card-body">
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:16}}>
            <div className="form-group">
              <label>{t('quotations.sample.tracking_number')}</label>
              <input value={tracking.tracking_number||''} onChange={e=>setTracking(tr=>({...tr,tracking_number:e.target.value}))} placeholder="SF1234567890CN" />
            </div>
            <div className="form-group">
              <label>{t('quotations.sample.carrier')}</label>
              <input value={tracking.carrier||''} onChange={e=>setTracking(tr=>({...tr,carrier:e.target.value}))} placeholder="DHL, FedEx, SF Express..." />
            </div>
            <div className="form-group">
              <label>{t('quotations.sample.dispatch_date')}</label>
              <input type="date" value={tracking.dispatched_at||''} onChange={e=>setTracking(tr=>({...tr,dispatched_at:e.target.value}))} />
            </div>
            <div className="form-group">
              <label>{t('quotations.sample.received_date')}</label>
              <input type="date" value={tracking.received_at||''} onChange={e=>setTracking(tr=>({...tr,received_at:e.target.value}))} />
            </div>
            <div className="form-group">
              <label>{t('quotations.sample.cost_usd')}</label>
              <input type="number" step="0.01" min="0" value={tracking.cost_usd||''} onChange={e=>setTracking(tr=>({...tr,cost_usd:e.target.value}))} placeholder="0.00" />
            </div>
            <div className="form-group" style={{gridColumn:'1/-1'}}>
              <label>{t('quotations.sample.notes')}</label>
              <input value={tracking.notes||''} onChange={e=>setTracking(tr=>({...tr,notes:e.target.value}))} placeholder={t('quotations.sample.notes_placeholder')} />
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">{t('quotations.sample.results_title')} ({sampleItems.length})</div></div>
        <div className="card-body" style={{padding:0}}>
          <table className="table-wrap" style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr style={{borderBottom:'2px solid var(--gray-100)'}}>
                <th style={{padding:'10px 16px',textAlign:'left',fontWeight:600,fontSize:12,color:'var(--gray-500)',textTransform:'uppercase'}}>{t('quotations.col.product')}</th>
                <th style={{padding:'10px 16px',textAlign:'left',fontWeight:600,fontSize:12,color:'var(--gray-500)',textTransform:'uppercase'}}>{t('quotations.col.spec')}</th>
                <th style={{padding:'10px 16px',textAlign:'center',fontWeight:600,fontSize:12,color:'var(--gray-500)',textTransform:'uppercase',width:80}}>{t('col.qty')}</th>
                <th style={{padding:'10px 16px',textAlign:'center',fontWeight:600,fontSize:12,color:'var(--gray-500)',textTransform:'uppercase',width:140}}>Status</th>
                <th style={{padding:'10px 16px',textAlign:'left',fontWeight:600,fontSize:12,color:'var(--gray-500)',textTransform:'uppercase'}}>{t('quotations.sample.notes')}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((item, idx) => {
                const statusOpt = SAMPLE_STATUS_OPTS.find(s => s.value === item.sample_status) || SAMPLE_STATUS_OPTS[0];
                return (
                  <tr key={item.id} style={{borderBottom:'1px solid var(--gray-100)'}}>
                    <td style={{padding:'10px 16px',fontWeight:600}}>{item.product}</td>
                    <td style={{padding:'10px 16px',fontSize:13,color:'var(--gray-500)'}}>{item.specification||'—'}</td>
                    <td style={{padding:'10px 16px',textAlign:'center'}}>
                      <input type="number" min="1" value={item.qty_sampled||1}
                        onChange={e=>setResult(idx,'qty_sampled',parseInt(e.target.value)||1)}
                        style={{width:60,textAlign:'center',border:'1px solid var(--gray-200)',borderRadius:6,padding:'4px 8px',fontSize:13}} />
                    </td>
                    <td style={{padding:'10px 16px',textAlign:'center'}}>
                      <select value={item.sample_status||'pending'}
                        onChange={e=>setResult(idx,'sample_status',e.target.value)}
                        style={{border:`1.5px solid ${statusOpt.color}`,borderRadius:6,padding:'4px 10px',fontSize:12,fontWeight:700,background:statusOpt.bg,color:statusOpt.color,cursor:'pointer'}}>
                        {SAMPLE_STATUS_OPTS.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                    <td style={{padding:'10px 16px'}}>
                      <input value={item.sample_notes||''} onChange={e=>setResult(idx,'sample_notes',e.target.value)}
                        placeholder={t('quotations.sample.obs_placeholder')}
                        style={{width:'100%',border:'1px solid var(--gray-200)',borderRadius:6,padding:'4px 8px',fontSize:13}} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{display:'flex',justifyContent:'flex-end'}}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? t('quotations.sample.saving_btn') : t('quotations.sample.save_btn')}
        </button>
      </div>
    </div>
  );
}
